import "./instrument";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "./index.css";
import "./i18n";
import { syncAnalyticsWithConsent } from "@/lib/analytics";
import { reloadForStaleChunk } from "@/lib/lazy-with-reload";

// PostHog only loads when cookie consent is "all" (see ConsentBanner).
syncAnalyticsWithConsent();

// Backup for the React.lazy wrapper: Vite fires `vite:preloadError` when a
// modulepreload for a hashed chunk fails (typically after a new deploy made
// the old file names disappear). Reload once so the browser fetches the new
// build instead of leaving the user on a broken page.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadForStaleChunk();
});

const isProd = import.meta.env.PROD;
// Resolution order MUST stay in lockstep with server/auth/middleware.ts.
// If client and server pick different keys from the same .env, Clerk's
// session-token refresh fails with an "infinite redirect loop / keys do
// not match" warning. Single ordered chain, prod-first:
//   VITE_CLERK_LIVE_PUBLISHABLE_KEY -> VITE_CLERK_PUBLISHABLE_KEY
// Live keys are domain-locked by Clerk to troopn.com, so dev/preview
// must stay on the test publishable key — using the live key on a
// replit.dev preview URL trips Clerk's "Production Keys are only
// allowed for domain troopn.com" guard and breaks sign-in.
const CLERK_PUBLISHABLE_KEY = isProd
  ? (import.meta.env.VITE_CLERK_LIVE_PUBLISHABLE_KEY || import.meta.env.VITE_CLERK_PUBLISHABLE_KEY)
  : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable");
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      signInUrl={isProd ? "https://accounts.troopn.com/sign-in" : undefined}
      signUpUrl={isProd ? "https://accounts.troopn.com/sign-up" : undefined}
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
    >
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ClerkProvider>
  </StrictMode>
);
